/* ============================================================================
   NEOGEN — GLOBAL NAV behavior  (real + usable)
   ----------------------------------------------------------------------------
   Staged 2026-07-07 (Fable 5). Target repo path: assets/neogen-nav.js
   SONNET-LIQUID-BACKLOG: §A cross-cutting + §G globals (nav ships on every page).
   Distilled + de-duplicated from the four inline nav scripts in
   _CANONICAL-NAV-FOOTER-v9-KIT.html into one asset.

   Behaviors:
     • Glass-on-scroll state machine — transparent over dark hero →
       violet glass while still over the hero (is-on-dark.is-scrolled) →
       cream glass once past the hero (is-past-hero). Wordmark bone↔violet swaps
       via .brand-mark--light / --dark (CSS driven by is-on-dark).
     • --nav-top pin under an optional announce band (.announce--top).
     • Scroll-direction hide/reveal (is-hidden).
     • Shop submenu keyboard a11y (aria-haspopup/expanded, Escape, focusout).
     • Mobile hamburger (is-menu-open).

   Contract (markup the header snippet must provide):
     nav#nav.nav            — root; add .is-on-dark on load IF page has a dark hero
     .hero                  — first full-bleed DARK hero (IO target). Omit on
                              light-hero pages; then set .is-past-hero on load.
     #navToggle             — hamburger button (aria-expanded)
     #navLinks              — the links container
     .nav__item--has-menu   — Shop menu wrapper
       .nav__shop-link[aria-haspopup][aria-expanded][aria-controls]
       .nav__submenu#navShopMenu[role=menu]
     .announce--top         — OPTIONAL announcement band above the nav
   ============================================================================ */
(function () {
  'use strict';

  var nav = document.getElementById('nav') || document.querySelector('.nav');
  if (!nav) return;

  /* Only a DARK hero drives is-on-dark. Interior pages (PDP/Compare/Science/
     About/Shop) also class their light heroes `.hero`, so a bare `.hero`
     selector wrongly put the violet-glass state on every page — the marker
     attribute keeps the IO scoped to genuinely dark full-bleed heroes. */
  var heroEl = document.querySelector('.hero[data-nav-dark]');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---- 1. on-dark / past-hero via IntersectionObserver ----------------- */
  if (heroEl && 'IntersectionObserver' in window) {
    var heroObs = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.intersectionRatio > 0.4) {
          nav.classList.add('is-on-dark');
          nav.classList.remove('is-past-hero');
        } else {
          nav.classList.remove('is-on-dark');
          nav.classList.add('is-past-hero');
        }
      });
    }, { threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] });
    heroObs.observe(heroEl);
  } else if (!heroEl) {
    // Light-hero page (PDP, Compare, Shop, About, Science, Bench Notes):
    // links must stay legible over a light ground from load.
    nav.classList.add('is-past-hero');
  }

  /* ---- 2. --nav-top pin under announce band + is-scrolled glass -------- */
  var band = document.querySelector('.announce--top');
  var bandH = band ? Math.round(band.getBoundingClientRect().height) : 0;
  function updatePin() {
    var y = window.scrollY || window.pageYOffset || 0;
    document.documentElement.style.setProperty('--nav-top', Math.max(bandH - y, 0) + 'px');
    nav.classList.toggle('is-scrolled', y > 4);
  }
  function remeasure() {
    if (band) bandH = Math.round(band.getBoundingClientRect().height);
    updatePin();
  }

  /* ---- 3. scroll-direction hide/reveal --------------------------------- */
  var lastY = window.scrollY || 0, lastTicked = lastY, ticking = false;
  function onScroll() {
    var y = window.scrollY || 0;
    updatePin();
    if (Math.abs(y - lastTicked) >= 4) {
      if (y > 200 && y > lastY) nav.classList.add('is-hidden');
      else nav.classList.remove('is-hidden');
      lastY = y; lastTicked = y;
    }
  }
  window.addEventListener('scroll', function () {
    if (!ticking) {
      window.requestAnimationFrame(function () { onScroll(); ticking = false; });
      ticking = true;
    }
  }, { passive: true });
  window.addEventListener('resize', remeasure);
  window.addEventListener('load', remeasure);
  remeasure();

  /* ---- 4. mobile hamburger --------------------------------------------- */
  var toggle = document.getElementById('navToggle');
  if (toggle) {
    toggle.addEventListener('click', function () {
      var open = nav.classList.toggle('is-menu-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      toggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu');
    });
    nav.querySelectorAll('.nav__links a').forEach(function (a) {
      a.addEventListener('click', function () {
        nav.classList.remove('is-menu-open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-label', 'Open menu');
      });
    });
  }

  /* ---- 5. Shop submenu (hover desktop / tap mobile) + full keyboard a11y */
  var mq = window.matchMedia('(max-width: 749px)');
  document.querySelectorAll('.nav__item--has-menu').forEach(function (item) {
    var link = item.querySelector('.nav__shop-link');
    var menu = item.querySelector('.nav__submenu');
    if (!link || !menu) return;
    function setExpanded(v) { link.setAttribute('aria-expanded', v ? 'true' : 'false'); }
    var items = menu.querySelectorAll('[role="menuitem"], a');

    link.addEventListener('click', function (e) {
      if (mq.matches) { e.preventDefault(); setExpanded(menu.classList.toggle('is-open')); }
    });
    item.addEventListener('mouseenter', function () {
      if (mq.matches) return;
      item.classList.remove('is-suppressed'); // fresh hover intent cancels an Escape suppression
      setExpanded(true);
    });
    item.addEventListener('mouseleave', function () {
      if (!mq.matches && !item.contains(document.activeElement)) setExpanded(false);
    });
    item.addEventListener('focusin', function () {
      if (mq.matches) return;
      if (item.classList.contains('is-suppressed')) return; // Escape's return-focus tick; keep aria-expanded false
      setExpanded(true);
    });
    item.addEventListener('focusout', function (e) {
      if (!item.contains(e.relatedTarget)) {
        setExpanded(false);
        menu.classList.remove('is-open');
        item.classList.remove('is-suppressed'); // focus has genuinely left; reset for next visit
      }
    });
    // Keyboard: Down opens + moves to first item; Escape closes + returns focus.
    link.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        item.classList.remove('is-suppressed'); // fresh keyboard-open intent cancels a prior Escape
        setExpanded(true);
        menu.classList.add('is-open');
        if (items[0]) items[0].focus();
      }
    });
    item.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') {
        menu.classList.remove('is-open');
        setExpanded(false);
        // link.focus() below re-enters .nav__item--has-menu, which would
        // otherwise re-trigger :focus-within and the menu would look like it
        // never closed. is-suppressed (assets/neogen-nav.css) force-hides the
        // submenu until the next real hover/ArrowDown intent clears it.
        item.classList.add('is-suppressed');
        link.focus();
      }
    });
  });

  /* Expose a tiny hook so the review shim / cart contract can co-exist. */
  window.neogenNav = { el: nav, reduceMotion: reduceMotion };
})();
